import { createSlice } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import { fetchSelfServeCreditBalance } from './thunks';

const initialState = {
	byDealershipId: {},
};

const createBalanceRecord = ({
	amountDollars,
	expiringCreditAmountDollars,
	expiringCreditExpirationDate,
	fetchedAt,
	isLoading = false,
	errorMessage = '',
}) => ({
	amountDollars,
	expiringCreditAmountDollars,
	expiringCreditExpirationDate,
	fetchedAt,
	isLoading,
	errorMessage,
});

const getMostRecentBalanceRecord = ({ currentRecord, hydratedRecord }) => {
	if (!currentRecord) return hydratedRecord;
	if (!hydratedRecord) return currentRecord;

	return hydratedRecord.fetchedAt > currentRecord.fetchedAt ? hydratedRecord : currentRecord;
};

export const selfServeCreditBalancesSlice = createSlice({
	name: 'selfServeCreditBalances',
	initialState,
	reducers: {
		invalidateBalance: (state, { payload: { dealershipId } }) => {
			delete state.byDealershipId[dealershipId];
		},
		upsertBalance: (state, { payload }) => {
			const { dealershipId, fetchedAt = Date.now(), ...balanceUpdate } = payload;
			const currentRecord = state.byDealershipId[dealershipId] || {};

			state.byDealershipId[dealershipId] = createBalanceRecord({
				...currentRecord,
				...balanceUpdate,
				fetchedAt,
				isLoading: false,
				errorMessage: '',
			});
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSelfServeCreditBalance.pending, (state, { meta }) => {
				const { dealershipId } = meta.arg;
				const currentRecord = state.byDealershipId[dealershipId] || {};
				state.byDealershipId[dealershipId] = createBalanceRecord({
					...currentRecord,
					isLoading: true,
					errorMessage: '',
				});
			})
			.addCase(fetchSelfServeCreditBalance.fulfilled, (state, { meta, payload }) => {
				const { dealershipId: requestedDealershipId } = meta.arg;
				const { dealershipId: responseDealershipId } = payload;

				if (String(requestedDealershipId) !== String(responseDealershipId)) {
					const requestedRecord = state.byDealershipId[requestedDealershipId];

					if (requestedRecord?.fetchedAt === undefined) {
						delete state.byDealershipId[requestedDealershipId];
					} else {
						state.byDealershipId[requestedDealershipId] = createBalanceRecord({
							...requestedRecord,
							isLoading: false,
						});
					}
				}

				const responseRecord = state.byDealershipId[responseDealershipId] || {};
				state.byDealershipId[responseDealershipId] = createBalanceRecord({
					...responseRecord,
					...payload,
					isLoading: false,
					errorMessage: '',
				});
			})
			.addCase(fetchSelfServeCreditBalance.rejected, (state, { meta, payload, error }) => {
				const { dealershipId } = meta.arg;
				const currentRecord = state.byDealershipId[dealershipId] || {};
				state.byDealershipId[dealershipId] = createBalanceRecord({
					...currentRecord,
					isLoading: false,
					errorMessage: payload || error.message || 'Unable to load self-serve credit balance.',
				});
			})
			.addCase(HYDRATE, (state, action) => {
				const hydratedBalances = action.payload?.selfServeCreditBalances?.byDealershipId || {};

				Object.entries(hydratedBalances).forEach(([dealershipId, hydratedRecord]) => {
					state.byDealershipId[dealershipId] = getMostRecentBalanceRecord({
						currentRecord: state.byDealershipId[dealershipId],
						hydratedRecord,
					});
				});
			});
	},
});

export const { invalidateBalance, upsertBalance } = selfServeCreditBalancesSlice.actions;

export default selfServeCreditBalancesSlice.reducer;
